"use client"

import { useSyncExternalStore } from "react"

/**
 * Device-local player preferences, edited from the main menu.
 *
 * Deliberately separate from audio-settings.ts (which the audio layer reads on
 * every sound and must stay tiny) and from game-state-persistence (which stores
 * an in-flight run, not preferences). Follows the same store shape as
 * audio-settings so components can subscribe without a provider.
 */

/** "random" re-rolls the hero on every new run - the historical behaviour. */
export type HeroPreference = "random" | string

export interface PlayerPrefs {
  /** Hero to spawn with on a new run, or "random" */
  preferredHero: HeroPreference
  /** Start runs at 2x fast-forward instead of 1x */
  startFastForwarded: boolean
  /** Suppress non-essential animations (menu transitions, pulses) */
  reducedMotion: boolean
  /** Pause the wave while a new-enemy card is open, so reading it costs no lives */
  pauseOnEnemyCard: boolean
}

const STORAGE_KEY = "pelf-player-prefs"

const DEFAULTS: PlayerPrefs = {
  preferredHero: "random",
  startFastForwarded: false,
  reducedMotion: false,
  pauseOnEnemyCard: true,
}

function load(): PlayerPrefs {
  if (typeof window === "undefined") return DEFAULTS
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY)
    if (!raw) return DEFAULTS
    const parsed = JSON.parse(raw) as Partial<PlayerPrefs>
    return {
      preferredHero:
        typeof parsed.preferredHero === "string" && parsed.preferredHero
          ? parsed.preferredHero
          : DEFAULTS.preferredHero,
      startFastForwarded: parsed.startFastForwarded === true,
      reducedMotion: parsed.reducedMotion === true,
      pauseOnEnemyCard: parsed.pauseOnEnemyCard !== false,
    }
  } catch {
    return DEFAULTS
  }
}

let prefs: PlayerPrefs = load()
const listeners = new Set<() => void>()

function persist(): void {
  try {
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(prefs))
  } catch {
    // Private browsing / storage full - preferences just won't survive reload
  }
}

export function getPlayerPrefs(): PlayerPrefs {
  return prefs
}

export function setPlayerPrefs(update: Partial<PlayerPrefs>): void {
  prefs = { ...prefs, ...update }
  persist()
  for (const listener of listeners) listener()
}

export function resetPlayerPrefs(): void {
  prefs = { ...DEFAULTS }
  persist()
  for (const listener of listeners) listener()
}

function subscribe(listener: () => void): () => void {
  listeners.add(listener)
  return () => listeners.delete(listener)
}

/** Reactive accessor for components. */
export function usePlayerPrefs(): PlayerPrefs {
  return useSyncExternalStore(subscribe, getPlayerPrefs, () => DEFAULTS)
}

/**
 * Resolve the hero a new run should start with.
 *
 * `availableHeroes` comes from the caller so this stays free of a data import
 * (and so tests can pin the roster). Falls back to a random pick when the
 * pinned hero no longer exists - e.g. a hero id was retired between releases.
 */
export function resolveStartingHero<T extends string>(availableHeroes: readonly T[]): T {
  const pinned = prefs.preferredHero
  if (pinned !== "random" && availableHeroes.includes(pinned as T)) {
    return pinned as T
  }
  return availableHeroes[Math.floor(Math.random() * availableHeroes.length)]
}
